// SPA route-change tracking for GA4 and Meta Pixel.
//
// Why this exists: Crayo uses manual hash routing (#/create, #/preview/...,
// etc.). gtag('config') and fbq('init') each auto-fire ONE page_view at
// document load. Neither SDK observes hashchange or popstate, so every
// in-app navigation after the first is invisible to both unless we fire
// the events ourselves. The downstream effect on GA4: every visit looks
// like one page_view at "/", and the funnel from / -> /create -> /preview
// -> /checkout -> /download collapses to a single step. Same on Pixel.
//
// Design:
//   - The initial page_view is left to gtag('config') + fbq('init') auto-fire.
//   - hashchange / popstate transitions go through trackRouteChange.
//   - Dedup against consecutive same-path events via `previousPath`.
//
// Consent gating happens upstream and is intentionally not duplicated here:
//   - GA4: Consent Mode v2 keeps gtag inert when analytics_storage is
//     denied. We can call gtag('event', 'page_view') unconditionally;
//     denied signals are dropped silently.
//   - Pixel: window.fbq is only defined after meta-pixel-init.js loads
//     fbevents.js, which only happens with marketing consent. We no-op
//     when fbq is missing.

let previousPath = null;

/**
 * Capture the initial route without emitting an event. Subsequent
 * trackRouteChange calls compare against this baseline.
 *
 * @param {string} initialPath
 */
export function initRouteTracking(initialPath) {
  previousPath = initialPath ?? null;
}

/**
 * Fire a virtual page_view in GA4 + Meta Pixel for an SPA route change.
 * No-ops when path is unchanged from the previous call (idempotent under
 * spurious double-firing of hashchange listeners).
 *
 * @param {string} path - new route, hash-stripped (e.g. "/create")
 */
export function trackRouteChange(path) {
  if (typeof window === 'undefined') return;
  if (!path) return;
  if (path === previousPath) return;
  previousPath = path;

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  if (typeof window.fbq === 'function') {
    window.fbq('track', 'PageView');
  }
}
